import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import HeaderServiceDropDown from '../../molecules/HeaderServiceDropDown/HeaderServiceDropDown';
import { ReactComponent as DOWNARROW } from '../../../app/assets/images/downarrow.svg'
import images from '../../../app/utlis/image';
import { trackClarityEvent } from '../../../app/utlis/ClarityAnalytics';
import { trackFirebaseEvent } from '../../../app/utlis/FirebaseAnalytics';
import { CLARITY_EVENTS } from '../../../app/utlis/EventTypes';

const Header = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const headerRef = useRef<HTMLDivElement>(null);
    const [dropdownVisible, setDropdownVisible] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
    const dropdownTimeoutRef = useRef<NodeJS.Timeout>();
    const isAbout = pathname === '/aboutus';

    useEffect(() => {
        setMobileOpen(false);
        setDropdownVisible(false);
        setMobileServicesOpen(false);
    }, [pathname]);

    const go = (path: string) => navigate(path);

    const handleGetInTouch = (source: 'header_desktop' | 'header_mobile') => {
        const payload = { source, page: pathname };
        trackClarityEvent(CLARITY_EVENTS.GET_IN_TOUCH_CLICKED, payload);
        trackFirebaseEvent(CLARITY_EVENTS.GET_IN_TOUCH_CLICKED, payload);
        go('/contactus');
    };

    const NAV_ITEMS = [
        { label: 'Home', path: '/' },

    ];

    const NAV_ITEMS_RIGHT = [
        { label: 'Portfolio', path: '/portfolio' },
        { label: 'About us', path: '/aboutus' },
        { label: 'Gallery', path: '/gallery' },
        { label: 'Career', path: '/career' },
        { label: 'Blog', path: '/blog' },
    ];

    const isActive = (path: string) => pathname === path;

    const navClass = (path: string) =>
        `cursor-pointer whitespace-nowrap transition-colors duration-200 hover:text-yellow ${isActive(path) ? 'text-yellow' : 'text-white'
        }`;

    return (
        <div className="relative" ref={headerRef}>
            <div
                className={`sticky top-0 z-50 flex items-center justify-between p-2 md:px-10 gap-4 shadow-[0px_14px_54px_0px_#00000021] transition-all duration-500 ${isAbout ? 'bg-[#24242486]' : 'bg-[#00000030]'
                    }`}
            >
                {/* Logo */}
                <img
                    src={images.LOGO}
                    alt="Logo"
                    onClick={() => go('/')}
                    className="w-32 md:w-48 bg-[#EDF1F5] p-2 rounded-lg cursor-pointer shrink-0"
                />

                {/* Desktop Nav */}
                <div className="hidden xl:flex w-full items-center justify-end gap-6 text-base font-medium">

                    {/* Left group: Home · Portfolio · About us */}
                    {NAV_ITEMS.map((item) => (
                        <div
                            key={item.path}
                            className={navClass(item.path)}
                            onClick={() => go(item.path)}
                        >
                            {item.label}
                        </div>
                    ))}

                    {/* Tech Services (dropdown) */}
                    <div
                        className={`flex items-center gap-1 cursor-pointer whitespace-nowrap transition-colors duration-200 hover:text-yellow ${dropdownVisible ? 'text-yellow' : 'text-white'
                            }`}
                        onMouseEnter={() => {
                            if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
                            setDropdownVisible(true);
                        }}
                        onMouseLeave={() => {
                            dropdownTimeoutRef.current = setTimeout(() => setDropdownVisible(false), 150);
                        }}
                        onClick={() => setDropdownVisible(!dropdownVisible)}
                    >
                        Tech Services
                        <DOWNARROW
                            width={16}
                            height={16}
                            className={`transition-transform duration-200 ${dropdownVisible ? 'rotate-180' : ''}`}
                        />
                    </div>

                    {/* Right group: Gallery · Career · Blog */}
                    {NAV_ITEMS_RIGHT.map((item) => (
                        <div
                            key={item.path}
                            className={navClass(item.path)}
                            onClick={() => go(item.path)}
                        >
                            {item.label}
                        </div>
                    ))}

                    {/* CTA */}
                    <div
                        className="bg-yellow text-black py-2.5 px-5 cursor-pointer whitespace-nowrap font-semibold hover:opacity-90 transition-opacity duration-200"
                        onClick={() => handleGetInTouch('header_desktop')}
                    >
                        Get in Touch
                    </div>
                </div>

                {/* Mobile hamburger */}
                <button
                    aria-label="Toggle menu"
                    className="xl:hidden text-white p-2 flex flex-col justify-center gap-1.5"
                    onClick={() => setMobileOpen(!mobileOpen)}
                >
                    <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`} />
                    <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${mobileOpen ? 'opacity-0' : ''}`} />
                    <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`} />
                </button>
            </div>

            {/* Desktop dropdown */}
            <div
                className={`absolute left-0 right-0 z-40 origin-top transition-all duration-300 ${dropdownVisible ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0 pointer-events-none'
                    }`}
                onMouseEnter={() => {
                    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
                    setDropdownVisible(true);
                }}
                onMouseLeave={() => {
                    dropdownTimeoutRef.current = setTimeout(() => setDropdownVisible(false), 150);
                }}
            >
                <HeaderServiceDropDown
                    setDropdownVisible={setDropdownVisible}
                    headerRef={headerRef}
                    mode="desktop"
                />
            </div>

            {/* Mobile / Tablet menu */}
            <div
                className={`xl:hidden absolute left-0 right-0 top-full z-40 bg-black/95 text-white origin-top transition-all duration-300 ${mobileOpen ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0 pointer-events-none'
                    }`}
            >
                <div className="max-h-[75vh] overflow-y-auto overscroll-contain">
                    <div className="flex flex-col px-6 py-4 gap-1">

                        {/* Home */}
                        {NAV_ITEMS.map(({ label, path }) => (
                            <div
                                key={path}
                                className={`py-3 border-b border-white/10 cursor-pointer transition-colors duration-200 hover:text-yellow ${isActive(path) ? 'text-yellow' : ''}`}
                                onClick={() => go(path)}
                            >
                                {label}
                            </div>
                        ))}

                        {/* Tech Services accordion */}
                        <div className="border-b border-white/10">
                            <div
                                className="py-3 flex items-center justify-between cursor-pointer select-none"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setMobileServicesOpen(prev => !prev);
                                }}
                            >
                                <span className={`transition-colors duration-200 ${mobileServicesOpen ? 'text-yellow' : ''}`}>
                                    Tech Services
                                </span>
                                <DOWNARROW
                                    width={16}
                                    height={16}
                                    className={`transition-transform duration-300 flex-shrink-0 ${mobileServicesOpen ? 'rotate-180' : ''}`}
                                />
                            </div>

                            {/* 
                    KEY FIX: use CSS grid rows to animate height from 0 → auto.
                    grid-rows-[0fr] = truly 0 height (no reserved space).
                    grid-rows-[1fr] = natural content height.
                    The inner div needs min-h-0 to allow collapsing.
                */}
                            <div
                                className={`grid transition-all duration-300 ${mobileServicesOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                                    }`}
                            >
                                <div className="min-h-0 overflow-hidden">
                                    <div
                                        className="border-l-2 border-yellow/40 pl-4 pb-2"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <HeaderServiceDropDown
                                            setDropdownVisible={setMobileServicesOpen}
                                            headerRef={headerRef}
                                            mode="mobile"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Remaining nav items — render immediately below, no gap */}
                        {NAV_ITEMS_RIGHT.map(({ label, path }) => (
                            <div
                                key={path}
                                className={`py-3 border-b border-white/10 cursor-pointer transition-colors duration-200 hover:text-yellow ${isActive(path) ? 'text-yellow' : ''}`}
                                onClick={() => go(path)}
                            >
                                {label}
                            </div>
                        ))}

                        <div
                            className="mt-4 py-3 text-center bg-yellow text-black font-semibold cursor-pointer hover:opacity-90 transition-opacity duration-200"
                            onClick={() => handleGetInTouch('header_mobile')}
                        >
                            Get in Touch
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default Header;